/* eslint-disable react-hooks/refs -- false positive: prop forwarding, not ref.current reads */
import {yupResolver} from '@hookform/resolvers/yup';
import {zodResolver} from '@hookform/resolvers/zod';
import i18next from 'i18next';
// eslint-disable-next-line no-restricted-imports -- Migrate to Zod
import * as Yup from 'yup';
import {z} from 'zod';

import {ReactNode, useCallback, useEffect, useId, useMemo, useRef} from 'react';
import {
  DefaultValues,
  FieldValues,
  FormProvider,
  KeepStateOptions,
  Path,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
  UseFormReset,
  UseFormReturn,
  ValidationMode,
} from 'react-hook-form';

import {isNilOrEmpty} from 'ramda-adjunct';

import {Nullish} from 'shared/web';

import {showNotification} from '../Notification/utils/events';
import {UnsavedChangesBlocker} from './components/UnsavedChangesBlocker';
import {
  useNavigateWithoutBlocker,
  NavigateWithoutBlocker,
  WithoutBlocker,
} from './hooks/useNavigateWithoutBlocker';
import {BlockerContext, FormControl, FormSubmitHandler, ValidationErrors} from './types';

export interface FormProps<TFieldValues extends FieldValues = FieldValues> {
  /**
   * @example
   * <Form>
   *  {(control) => (
   *    <FormField control={control} name="title" type="text" />
   *  )}
   * </Form>
   */
  children: (
    control: FormControl<TFieldValues>,
    formApi: UseFormReturn<TFieldValues>,
    navigateWithoutBlocker: NavigateWithoutBlocker,
    withoutBlocker: WithoutBlocker
  ) => ReactNode;
  defaultValues?: DefaultValues<TFieldValues> | Nullish;
  values?: TFieldValues | Nullish;
  resetOptions?: KeepStateOptions;
  schema?: Yup.AnyObjectSchema;
  mode?: keyof ValidationMode;
  /**
   * @about onSubmit function has to be async and typed as FormSubmitHandler<YourFormValues>
   * @example
   * const onSubmit: FormSubmitHandler<LoginFormValues> = async (values) => {
   *   await loginToApp(...)
   * };
   */
  onSubmit?: FormSubmitHandler<TFieldValues>;
  onInvalidSubmit?: SubmitErrorHandler<TFieldValues>;
  onChange?: (
    values: TFieldValues,
    setErrors: (errors: ValidationErrors | null) => void,
    reset: UseFormReset<TFieldValues>,
    formApi: UseFormReturn<TFieldValues>
  ) => void;
  isFullHeight?: boolean;
  experimentalZodSchema?: z.ZodSchema<TFieldValues>;
  isDisabled?: boolean;
  /**
   * Enables a blocker dialog when user tries to navigate away from a form with unsaved changes.
   *
   * **IMPORTANT: If your onSubmit handler calls `navigate()`, you MUST check the `blockerContext.wasSubmittedFromBlocker` flag
   * to prevent race conditions between the blocker's navigation and your handler's navigation.**
   *
   * @example
   * // ✅ CORRECT - Checking wasSubmittedFromBlocker before navigating
   * const handleSubmit: FormSubmitHandler<FormType> = async (values, setErrors, reset, blockerContext) => {
   *   await saveMutation(values)
   *     .unwrap()
   *     .then(() => {
   *       showNotification.success();
   *       // Only navigate if NOT submitting from blocker (blocker will handle navigation)
   *            if (blockerContext?.wasSubmittedFromBlocker){
            return;
          }
   *       navigate(settingsRoutes.advertisingQuickPresets);
   *     })
   *     .catch(handleApiError);
   * };
   *
   * @example
   * // ❌ WRONG - Navigating without checking the flag (causes race condition)
   * const handleSubmit: FormSubmitHandler<FormType> = async (values) => {
   *   await saveMutation(values)
   *     .unwrap()
   *     .then(() => {
   *       navigate(settingsRoutes.myRoute); // Race condition! Don't do this!
   *     });
   * };
   *
   * @example
   * // ✅ CORRECT - Form that stays on same page (no navigation needed)
   * const handleSubmit: FormSubmitHandler<FormType> = async (values, setErrors, reset) => {
   *   await saveMutation(values)
   *     .unwrap()
   *     .then(() => {
   *       showNotification.success();
   *       reset(undefined, {keepValues: true}); // Clears dirty state
   *       // No navigation - form stays open
   *     })
   *     .catch(handleApiError);
   * };
   *
   * @example
   * // ✅ CORRECT - Preventing navigation on API error via blockerContext.reportApiFailure
   * const handleSubmit: FormSubmitHandler<FormType> = async (values, setErrors, reset, blockerContext) => {
   *   await saveMutation(values)
   *     .unwrap()
   *     .then(() => {
   *       showNotification.success();
   *            if (blockerContext?.wasSubmittedFromBlocker){
            return;
          }
   *       navigate(myRoute);
   *     })
   *     .catch((e: ApiError) => handleApiError(e, {signalApiError: blockerContext?.reportApiFailure}));
   */
  shouldWatchForUnsavedChanges?: boolean;
}

export function Form<TFieldValues extends FieldValues = FieldValues>(
  props: FormProps<TFieldValues>
) {
  const formId = useId();
  const onSubmitHandler = props.onSubmit;
  const onInvalidSubmitHandler = props.onInvalidSubmit;
  const onChangeHandler = props.onChange;

  // TODO: Temporary solution for handling resolvers.
  // https://carvago.atlassian.net/browse/T20-70580
  // This will be replaced once zod validation schemas will be implemented everywhere.
  const getResolver = () => {
    if (props.experimentalZodSchema) {
      return zodResolver(props.experimentalZodSchema);
    }
    if (props.schema) {
      return yupResolver(props.schema);
    }
    return undefined;
  };

  const formApi = useForm<TFieldValues>({
    mode: props.mode ?? 'onSubmit',
    resolver: getResolver(),
    defaultValues: props.defaultValues ?? undefined,
    values: props.values ?? undefined,
    resetOptions: props.resetOptions,
    disabled: props.isDisabled,
  });

  useEffect(() => {
    if (isNilOrEmpty(props.values)) {
      formApi.reset(undefined);
    }
  }, [props.values]);

  const {navigateWithoutBlocker, withoutBlocker, shouldNavigateWithoutBlockerRef} =
    useNavigateWithoutBlocker();

  const apiErrorOccurredRef = useRef<boolean>(false);

  const setErrors = useCallback(
    (validationErrors: ValidationErrors | null) => {
      validationErrors?.forEach((error) => {
        formApi.setError(error.name as Path<TFieldValues>, {message: error.message});
      });
    },
    [formApi]
  );

  // Note: This handler is used for native <form> submit events (submit button click).
  // It does NOT check apiErrorOccurredRef — that check only applies to submitForm,
  // which is called by UnsavedChangesBlocker. Direct form submits don't gate navigation.
  const onSubmit: SubmitHandler<TFieldValues> = useCallback(
    async (values) => {
      await onSubmitHandler?.(values, setErrors, formApi.reset);
    },
    [onSubmitHandler, setErrors, formApi.reset]
  );

  const onInvalidSubmit: SubmitErrorHandler<TFieldValues> = useCallback(
    async (errors, event) => {
      await onInvalidSubmitHandler?.(errors, event);

      if (i18next.exists('errors.invalid_submit')) {
        showNotification.warning(i18next.t('errors.invalid_submit'));
      }

      if (process.env.ENV_TYPE === 'dev' || process.env.ENV_TYPE === 'stage') {
        console.warn(
          'Form submit failed due to these errors: ',
          JSON.stringify(errors, undefined, 2)
        );
      }
    },
    [onInvalidSubmitHandler]
  );

  /**
   * Programmatically submits the form and returns a success indicator.
   * Used by UnsavedChangesBlocker to save before navigation.
   *
   * @param blockerContext - Context injected by the UnsavedChangesBlocker
   * @param blockerContext.wasSubmittedFromBlocker - Whether this submission is triggered from the blocker dialog
   * @returns Promise<boolean> - true if submission succeeded, false if validation failed or
   *   blockerContext.reportApiFailure() was called during the handler.
   *
   * @note API errors prevent navigation when the submit handler calls `blockerContext.reportApiFailure()`.
   * Without it, API errors only show toast notifications and navigation proceeds (legacy behavior).
   */
  const submitForm = useCallback(
    (blockerContext?: Pick<BlockerContext, 'wasSubmittedFromBlocker'>): Promise<boolean> =>
      new Promise((resolve) => {
        // Reset the API error signal before each submission attempt.
        apiErrorOccurredRef.current = false;

        const reportApiFailure = () => {
          apiErrorOccurredRef.current = true;
        };

        formApi.handleSubmit(
          async (values) => {
            await onSubmitHandler?.(values, setErrors, formApi.reset, {
              ...blockerContext,
              reportApiFailure,
            });
            // If reportApiFailure() was called during the handler,
            // resolve false so the blocker does NOT proceed with navigation.
            if (apiErrorOccurredRef.current) {
              resolve(false);
              return;
            }
            resolve(true);
          },
          (errors, event) => {
            // Validation failed.
            onInvalidSubmit(errors, event);
            resolve(false);
          }
        )();
      }),
    [formApi, onSubmitHandler, setErrors, onInvalidSubmit]
  );

  const onChange = useCallback(() => {
    onChangeHandler?.(formApi.getValues(), setErrors, formApi.reset, formApi);
  }, [formApi, onChangeHandler, setErrors]);

  const control = useMemo(
    () => Object.assign(formApi.control, {formId, onChange, submitForm}),
    [formApi.control, formId, onChange, submitForm]
  );

  return (
    <FormProvider {...formApi}>
      <form
        css={props.isFullHeight ? {height: '100%'} : undefined}
        onSubmitCapture={(event) => {
          if (event.target instanceof HTMLElement && event.target.getAttribute('id') === formId) {
            formApi.handleSubmit(onSubmit, onInvalidSubmit)(event);
          }
        }}
        id={formId}
        noValidate
      >
        {props.children(control, formApi, navigateWithoutBlocker, withoutBlocker)}
      </form>

      <UnsavedChangesBlocker
        isDisabled={!props.shouldWatchForUnsavedChanges}
        shouldNavigateWithoutBlockerRef={shouldNavigateWithoutBlockerRef}
      />
    </FormProvider>
  );
}
